import { useState, useEffect, useCallback } from 'react';
import './Requests.scss';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import ChangeAmountModal from '../../components/ChangeAmountModal/ChangeAmountModal';
import Pagination from '../../components/Layout/Pagination';
import PageToolbar from '../../components/Layout/PageToolbar';
import Toast from '../../components/Toast/Toast';
import CopyButton from '../../components/CopyButton/CopyButton';
import { useTranslation } from 'react-i18next';

// Утилита: извлекает текст из HTML строки
const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};


// Маппинг статусов на визуальные стили
const STATUS_CONFIG = {
    new:                { label: 'Новая',       color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)',  textColor: '#8b5cf6', rowBg: 'rgba(139,92,246,0.04)' },
    payment_wait:       { label: 'Ожидает',     color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)',  textColor: '#8b5cf6', rowBg: 'rgba(139,92,246,0.04)' },
    changed:            { label: 'Изменена',    color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  textColor: '#f59e0b', rowBg: 'rgba(245,158,11,0.06)' },
    payed:              { label: 'Проверяем',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   textColor: '#22c55e', rowBg: 'rgba(34,197,94,0.04)' },
    need_success:       { label: 'Завершить',   color: '#22c55e', bg: 'rgba(34,197,94,0.15)',   textColor: '#22c55e', rowBg: 'rgba(34,197,94,0.06)' },
    trouble:            { label: 'Диспут',      color: '#ffffff', bg: '#ef4444',                textColor: '#ffffff', rowBg: 'rgba(239,68,68,0.08)' },
    success:            { label: 'Завершена',   color: '#ffffff', bg: '#22c55e',                textColor: '#ffffff', rowBg: 'rgba(34,197,94,0.04)' },
    canceled:           { label: 'Отменена',    color: '#9ca3af', bg: 'rgba(156,163,175,0.12)', textColor: '#9ca3af', rowBg: 'rgba(156,163,175,0.04)' },
    autocanceled:       { label: 'Авто-отмена', color: '#9ca3af', bg: 'rgba(156,163,175,0.12)', textColor: '#9ca3af', rowBg: 'rgba(156,163,175,0.04)' },
    success_by_support: { label: 'Поддержкой',  color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   textColor: '#06b6d4', rowBg: 'rgba(6,182,212,0.04)' },
};

// Парсинг суммы из HTML "<b style="font-size: 1.2em">7500 RUB</b>"
const parseAmount = (html) => {
    if (!html) return { amount: '', currency: '' };
    const text = stripHtml(html);
    const match = text.match(/^([\d\s.,]+)\s*(\w+)$/);
    if (match) return { amount: match[1].trim(), currency: match[2] };
    return { amount: text, currency: '' };
};

// Парсинг реквизитов из HTML "В работе<br><b>Тестовая карта RUB оператор 1</b>"
const parseCredentials = (html) => {
    if (!html) return { status: '', name: '' };
    const parts = html.split(/<br\s*\/?>/i);
    const status = stripHtml(parts[0] || '');
    const name = stripHtml(parts.slice(1).join(' ') || '');
    return { status, name };
};

// Парсинг даты из "Создана: 09.12.2025 09:17:29" или нескольких строк
const parseDates = (html) => {
    if (!html) return [];
    const text = stripHtml(html);
    const regex = /(\S+?):\s*(\d{2}\.\d{2}\.\d{4})\s+(\d{2}:\d{2}:\d{2})/g;
    const dates = [];
    let m;
    while ((m = regex.exec(text)) !== null) {
        dates.push({ label: m[1], date: m[2], time: m[3] });
    }
    if (dates.length === 0 && text.trim()) {
        dates.push({ label: '', date: text.trim(), time: '' });
    }
    return dates;
};

Modal.setAppElement('#root');

const ConfirmationModal = ({ isOpen, onRequestClose, onConfirm, price, textLabel, textAction }) => {
    const { t } = useTranslation('requests');
    const cleanPrice = stripHtml(price);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            contentLabel={textLabel}
            className="confirmation-modal"
            overlayClassName="confirmation-modal-overlay"
        >
            <div className="confirmation-modal-header">
                <h2 className="confirmation-modal-title">{textLabel}</h2>
                <button className="confirmation-modal-close" onClick={onRequestClose}>×</button>
            </div>
            <div className="confirmation-modal-content">
                <p>{t('requests:modals.confirmText', { action: textAction })} <strong>{cleanPrice}</strong>?</p>
            </div>
            <div className="confirmation-modal-actions">
                <button className="confirmation-modal-button confirm" onClick={onConfirm}>{t('common:yes')}</button>
                <button className="confirmation-modal-button cancel" onClick={onRequestClose}>{t('common:no')}</button>
            </div>
        </Modal>
    );
};

export default function Requests() {
    const { t } = useTranslation('requests');

    const [requestsData, setRequestsData] = useState([]);

    const theme = useSelector((state) => state.APP_THEME);
    const api_url = useSelector((state) => state.API_URL);
    const api_url_no_ra = useSelector((state) => state.API_URL_NO_RAN);
    const user_can_change_amount = useSelector((state) => state.USER_CAN_CHANGE_AMOUNT);

    //const [requestsData, setRequestsData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [modalIsOpen1, setModalIsOpen1] = useState(false); // закрыть заявку
    const [modalIsOpen2, setModalIsOpen2] = useState(false); // отменить заявку
    const [modalIsOpen3, setModalIsOpen3] = useState(false); // закрыть по видео
    const [modalIsOpen4, setModalIsOpen4] = useState(false); // запросить чек
    const [selectedRequestPrice, setSelectedRequestPrice] = useState('');
    const [selectedRequestPk, setSelectedRequestPk] = useState('');

    const [searchNumber, setSearchNumber] = useState('');
    // const [priceFrom, setPriceFrom] = useState('');
    // const [priceTo, setPriceTo] = useState('');
    const [selectedButtons, setSelectedButtons] = useState([]);
    const [statusValues, setStatusValues] = useState([]);
    // Сверху 5 стейтов для фильтров

    const [changeAmountModalOpen, setChangeAmountModalOpen] = useState(false);
    const [selectedRequestForChange, setSelectedRequestForChange] = useState(null);

    const [errorFetchingRequests, setErrorFetchingRequests] = useState(false); // стейт для вывода алерта об ошибки запроса данных заявок
    const [errorClosingReq, setErrorClosingReq] = useState(false); // стейт для вывода алерта об ошибки закрытия заявки
    const [closingReqText, setClosingReqText] = useState(''); // стейт для вывода текста закрытия заявки 
    const [closingReqIsDone, setClosingReqIsDone] = useState(false);

    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(1);
    const [reqRowsCount, setReqRowsCount] = useState(0);

    const [isReqInCheck, setIsReqInCheck] = useState(false);


    const [inCheckCount, setInCheckCount] = useState(0);

    const handleButtonClick = (button) => {
        const buttonValuesMap = {
            'Активно': ['new', 'payment_wait', 'changed'],
            'Успешно завершено': ['payed', 'success', 'need_success'],
            'Отмена': ['autocanceled', 'canceled'],
            'Завершено поддержкой': ['success_by_support']
        };

        const buttonValues = buttonValuesMap[button];

        if (selectedButtons.includes(button)) {
            // Убираем кнопку из selectedButtons и её значения из statusValues
            setSelectedButtons(selectedButtons.filter((item) => item !== button));
            setStatusValues(statusValues.filter((item) => !buttonValues.includes(item)));
        } else {
            // Добавляем кнопку в selectedButtons и её значения в statusValues
            setSelectedButtons([...selectedButtons, button]);
            setStatusValues([...statusValues, ...buttonValues]);
        }
    };




    const generateColumns = useCallback((data) => {
        if (data.length > 0) {
            const firstItem = data[0];
            const generatedColumns = Object.keys(firstItem).slice(1).map(key => ({
                id: key,
                label: firstItem[key]?.label || key,
                minWidth: 170,
                align: 'left',
                format: (value) => <div>{stripHtml(String(value || ''))}</div>,
                formatStatus: (value) => <div className={`statusCell ${value}`}>{t('requests:statusesDefault.' + value)}</div>,
            }));
            setColumns(generatedColumns);
        }
    }, [t]);

    const navigate = useNavigate();

    useEffect(() => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) {
            navigate('/auth');
            localStorage.clear();
        }
    }, [navigate]);

    const createDataFromRequest = useCallback((request) => {
        let data = {};

        for (let key in request) {
            if (key === 'pk') {
                data[key] = request[key]; // Если pk — записываем как есть
            } else {
                data[key] = request[key]?.value; // Безопасно получаем .value
            }
        }

        return data;
    }, []);

    const [rows, setRows] = useState(requestsData.map(createDataFromRequest));

    const getRequestsData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(api_url + `exchange/exchangedeal/info/?page=${pageIndex}&page_size=${pageSize}&status__in=${statusValues.join(',')}&search=${encodeURIComponent(searchNumber)}${isReqInCheck ? '&is_under_verification=1' : ''}`, {
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    // Обработка ошибки 401
                    localStorage.clear();
                    navigate('/auth');
                }
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            setRequestsData(data.results);
            setRows(data.results.map(createDataFromRequest));
            setReqRowsCount(data.count);

            //Генерация колонок
            generateColumns(data.results); // requestsData - заменить на data

        } catch (error) {
            // console.error('Error fetching data:', error);
            setErrorFetchingRequests(true);
        }
    }, [api_url, pageIndex, pageSize, statusValues, searchNumber, isReqInCheck, navigate, generateColumns, createDataFromRequest]);

    useProtectedInterval(getRequestsData, [pageIndex, pageSize, statusValues, searchNumber, modalIsOpen1, modalIsOpen2, modalIsOpen3, modalIsOpen4]);

    const fetchInCheckData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(`${api_url}exchange/exchangedeal/info/?is_under_verification=1`, {
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            if (data && typeof data.count === 'number') {
                setInCheckCount(data.count);
            }
        } catch (error) {
            // console.error('Ошибка при выполнении запроса:', error);
        }
    }, [api_url]);

    useProtectedInterval(fetchInCheckData, []);

    function closeRequest() {
        const jwtToken = localStorage.getItem('accessToken');
        fetch(`${api_url_no_ra}/api/v3/exchange_request/close/${selectedRequestPk}/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                setErrorClosingReq(true);
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            setClosingReqIsDone(true);
            setClosingReqText(data.text);
            // Обновите состояние или выполните другие действия после успешного закрытия заявки
        })
        .catch(error => {
            // console.error('Error closing request:', error);
            // Обработайте ошибку, если запрос завершился неудачно
        })
        .finally(() => {
            setModalIsOpen1(false);
        });
    }

    function cancelRequest() {
        const jwtToken = localStorage.getItem('accessToken');
        fetch(`${api_url_no_ra}/api/v3/exchange_request/cancel/${selectedRequestPk}/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                setErrorClosingReq(true);
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            setClosingReqIsDone(true);
            setClosingReqText(data.text);
            // Обновите состояние или выполните другие действия после успешного закрытия заявки
        })
        .catch(error => {
            // console.error('Error closing request:', error);
            // Обработайте ошибку, если запрос завершился неудачно
        })
        .finally(() => {
            setModalIsOpen2(false);
        });
    }

    function closeRequestByVideo() {
        const jwtToken = localStorage.getItem('accessToken');
        fetch(`${api_url_no_ra}/api/v3/exchange_request/close-by-video/${selectedRequestPk}/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                setErrorClosingReq(true);
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            setClosingReqIsDone(true);
            setClosingReqText(data.text);
            // Обновите состояние или выполните другие действия после успешного закрытия заявки
        })
        .catch(error => {
            // console.error('Error closing request:', error);
            // Обработайте ошибку, если запрос завершился неудачно
        })
        .finally(() => {
            setModalIsOpen3(false);
        });
    }

    function closeRequestByReceipt() {
        const jwtToken = localStorage.getItem('accessToken');
        fetch(`${api_url_no_ra}/api/v3/exchange_request/close-by-receipt/${selectedRequestPk}/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                setErrorClosingReq(true);
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            setClosingReqIsDone(true);
            setClosingReqText(data.text);
            // Обновите состояние или выполните другие действия после успешного закрытия заявки
        })
        .catch(error => {
            // console.error('Error closing request:', error);
            // Обработайте ошибку, если запрос завершился неудачно
        })
        .finally(() => {
            setModalIsOpen4(false);
        });
    }

    async function changeRequestAmount(dealId, newAmount) {
        const jwtToken = localStorage.getItem('accessToken');
        return fetch(`${api_url_no_ra}/client/api/exchange/exchangedeal/change_request_amount/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                deal_id: dealId,
                amount: newAmount
            })
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                setClosingReqIsDone(true);
                setClosingReqText(t('requests:errors.amountChangeSuccess'));
                getRequestsData(); // Refresh table
                setChangeAmountModalOpen(false);
            } else {
                setErrorClosingReq(true);
                throw new Error(t('requests:errors.amountChangeError'));
            }
        })
        .catch(error => {
            console.error('Error changing request amount:', error);
            setErrorClosingReq(true);
            throw error;
        });
    }

    const handleClearFilters = () => {
        setSearchNumber('');
        setSelectedButtons([]);
        setStatusValues([]);
        setIsReqInCheck(false);
    }

    return (
        <div className={theme === 'light' ? "Requests" : "Requests dark"}>
            <Toast show={errorFetchingRequests} type="error" message={t('requests:errors.fetchError')} onClose={() => setErrorFetchingRequests(false)} />
            <Toast show={errorClosingReq} type="error" message={t('requests:errors.closeError')} onClose={() => setErrorClosingReq(false)} />
            <Toast show={closingReqIsDone} type="success" message={closingReqText} onClose={() => setClosingReqIsDone(false)} />
            <div className="content">
                <PageToolbar
                    filters={[
                        {label: t('requests:filters.active'), value: 'Активно'},
                        {label: t('requests:filters.successCompleted'), value: 'Успешно завершено'},
                        {label: t('requests:filters.cancel'), value: 'Отмена'},
                        {label: t('requests:filters.completedBySupport'), value: 'Завершено поддержкой'}
                    ]}
                    onFilterChange={(filter) => {setIsReqInCheck(false); handleButtonClick(filter)}}
                    activeFilters={selectedButtons}
                    searchValue={searchNumber}
                    onSearchChange={setSearchNumber}
                    searchPlaceholder={t('requests:searchPlaceholder')}
                    onClearAll={handleClearFilters}
                    extraButtons={
                        <button
                            onClick={() => {
                                setIsReqInCheck(!isReqInCheck);
                                setSelectedButtons([]);
                                setStatusValues([]);
                            }}
                            className={isReqInCheck ? 'isInCheck active' : 'isInCheck'}
                        >
                            {t('requests:disputes')}
                            {inCheckCount !== 0 && <span style={{color: "red", fontWeight: "700"}}>{inCheckCount}</span>}
                        </button>
                    }
                />
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />
                <Paper className="req-table-paper" sx={{ width: '100%', overflow: 'hidden', background: theme === 'light' ? 'white' : 'rgba(31, 38, 48, 1)', borderRadius: '12px' }}>
                    <TableContainer>
                        <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                key={column.id}
                                align={column.align}
                                className="req-table-head"
                                style={theme === 'light' ? {color: "var(--text-secondary)", background: "#f1f3f5", fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'} : {color: "var(--text-secondary)", background: "rgb(22, 29, 38)", fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px'}}
                                >
                                {column.label}
                                </TableCell>
                            ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows
                            .map((row, index) => {
                                const statusConf = STATUS_CONFIG[row.status] || {};
                                const rowStyle = statusConf.rowBg ? { background: statusConf.rowBg } : {};
                                return (
                                <TableRow hover tabIndex={-1} key={row.uuid_info || index} style={rowStyle} className={`req-row status-${row.status}`}>
                                    {columns.map((column) => {
                                    const value = row[column.id];
                                    const cellStyle = theme === 'light' ? {color: "var(--text-primary)"} : {color: "var(--text-primary)"};

                                    // Номер заявки — крупно + кнопка копирования
                                    if (column.id === 'uuid_info') {
                                        return (
                                            <TableCell key={column.id} style={cellStyle}>
                                                <div className="req-cell-id">
                                                    <span className="req-id-number">#{row.pk}</span>
                                                    <CopyButton text={String(row.pk)} size="small" />
                                                </div>
                                            </TableCell>
                                        );
                                    }

                                    // Сумма — крупно и жирно
                                    if (column.id === 'payment_amount_info') {
                                        const { amount, currency } = parseAmount(value);
                                        return (
                                            <TableCell key={column.id} style={cellStyle}>
                                                <div className="req-cell-amount">
                                                    <span className="req-amount-value">{amount}</span>
                                                    <span className="req-amount-currency">{currency}</span>
                                                </div>
                                            </TableCell>
                                        );
                                    }

                                    // Статус — цветной badge с заливкой
                                    if (column.id === 'status') {
                                        return (
                                            <TableCell key={column.id} style={cellStyle}>
                                                <span
                                                    className={`req-status-badge status-${value}`}
                                                    style={{
                                                        background: statusConf.bg || '#e5e7eb',
                                                        color: statusConf.textColor || '#374151',
                                                    }}
                                                >
                                                    {t('requests:statuses.' + value, value)}
                                                </span>
                                            </TableCell>
                                        );
                                    }

                                    // Реквизиты — структурированный блок
                                    if (column.id === 'number_info') {
                                        const creds = parseCredentials(value);
                                        return (
                                            <TableCell key={column.id} style={cellStyle}>
                                                <div className="req-cell-creds">
                                                    <span className="req-creds-status">{creds.status}</span>
                                                    <span className="req-creds-name">{creds.name}</span>
                                                </div>
                                            </TableCell>
                                        );
                                    }

                                    // Автозакрытие — компактный индикатор
                                    if (column.id === 'credentials_can_autoclose') {
                                        const text = stripHtml(value);
                                        const isEnabled = text === 'Включено';
                                        return (
                                            <TableCell key={column.id} style={cellStyle}>
                                                <span className={`req-autoclose ${isEnabled ? 'enabled' : 'disabled'}`}>
                                                    <span className="req-autoclose-dot" />
                                                    {text}
                                                </span>
                                            </TableCell>
                                        );
                                    }

                                    // Дата — компактно с тултипом
                                    if (column.id === 'operator_date_info') {
                                        const dates = parseDates(value);
                                        return (
                                            <TableCell key={column.id} style={cellStyle}>
                                                <div className="req-cell-dates">
                                                    {dates.map((d, i) => (
                                                        <Tooltip key={i} title={d.label || t('common:date')} arrow placement="top">
                                                            <div className={`req-date-item ${d.label.toLowerCase()}`}>
                                                                <span className="req-date-value">{d.date}</span>
                                                                <span className="req-date-time">{d.time}</span>
                                                            </div>
                                                        </Tooltip>
                                                    ))}
                                                </div>
                                            </TableCell>
                                        );
                                    }

                                    // Действия — кнопки
                                    if (column.id === 'actions_column' && row.status !== 'success' && row.status !== 'success_by_support') {
                                        return (
                                            <TableCell key={column.id} style={cellStyle}>
                                                <div className="req-actions">
                                                    {user_can_change_amount === 'yes' && (
                                                        <button className='req-action-btn action-edit' onClick={() => {
                                                            setChangeAmountModalOpen(true);
                                                            setSelectedRequestForChange(row);
                                                        }}>{t('requests:actions.changeAmount')}</button>
                                                    )}
                                                    <button className='req-action-btn action-close' onClick={() => {setModalIsOpen1(true); setSelectedRequestPrice(row.payment_amount_info); setSelectedRequestPk(row.pk)}}>{t('requests:actions.close')}</button>
                                                    {isReqInCheck && (
                                                        <button className='req-action-btn action-cancel' onClick={() => {setModalIsOpen2(true); setSelectedRequestPrice(row.payment_amount_info); setSelectedRequestPk(row.pk)}}>{t('requests:actions.cancel')}</button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        );
                                    }

                                    // Действия для завершённых — пусто
                                    if (column.id === 'actions_column') {
                                        return <TableCell key={column.id} style={cellStyle}><span className="req-done-label">—</span></TableCell>;
                                    }

                                    // Прочие колонки — fallback
                                    return (
                                        <TableCell key={column.id} align={column.align} style={cellStyle}>
                                            {column.format ? column.format(value) : value}
                                        </TableCell>
                                    );
                                    })}
                                </TableRow>
                                );
                            })}
                        </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={reqRowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />
                <ConfirmationModal
                    isOpen={modalIsOpen1} 
                    onRequestClose={() => setModalIsOpen1(false)} 
                    onConfirm={closeRequest} 
                    price={selectedRequestPrice} 
                    textLabel={t('requests:modals.confirmClose')}
                    textAction={t('requests:modals.actionClose')}
                />
                <ConfirmationModal
                    isOpen={modalIsOpen2} 
                    onRequestClose={() => setModalIsOpen2(false)} 
                    onConfirm={cancelRequest} 
                    price={selectedRequestPrice} 
                    textLabel={t('requests:modals.confirmCancel')}
                    textAction={t('requests:modals.actionCancel')}
                />
                <ConfirmationModal
                    isOpen={modalIsOpen3} 
                    onRequestClose={() => setModalIsOpen3(false)} 
                    onConfirm={closeRequestByVideo} 
                    price={selectedRequestPrice} 
                    textLabel={t('requests:modals.confirmCloseByVideo')}
                    textAction={t('requests:modals.actionClose')}
                />

                <ConfirmationModal
                    isOpen={modalIsOpen4}
                    onRequestClose={() => setModalIsOpen4(false)}
                    onConfirm={closeRequestByReceipt}
                    price={selectedRequestPrice}
                    textLabel={t('requests:modals.confirmReceipt')}
                    textAction={t('requests:modals.actionRequest')}
                />
                <ChangeAmountModal
                    isOpen={changeAmountModalOpen}
                    onClose={() => setChangeAmountModalOpen(false)}
                    request={selectedRequestForChange}
                    onSubmit={changeRequestAmount}
                />
            </div>
        </div>
    )
}
